import { useCallback } from "react";
import {
  filterOutByParticipantSid,
  hasParticipantBySid,
  remoteParticipantToDiagnosticInfo,
} from "../../video-sdk/core/utils";
import { RemoteParticipant } from "../../video-sdk/core/room/participant/RemoteParticipant";
import { useAtomCallback } from "jotai/utils";
import { useSetAtom } from "jotai";
import {
  participantsAtom,
  remoteParticipantConnectionStatesByIdAtom,
} from "../../video-sdk/core/room/participant/atoms";
import { useAppDependenciesContext } from "../../state";
import { TrackingEvents } from "../../events";
import { parseIdentity } from "../../video-sdk/core/room/participant/utils";
import { omit } from "remeda";

/**
 * Handles when a remote participant is disconnected from the room i.e. no
 * longer in the room.
 */
export function useParticipantDisconnectedEventHandler() {
  const setParticipants = useSetAtom(participantsAtom);
  const getParticipants = useAtomCallback(
    useCallback((get) => get(participantsAtom), []),
  );
  const setRemoteParticipantConnectionStatesById = useSetAtom(
    remoteParticipantConnectionStatesByIdAtom,
  );
  const { sendLoggingEvents, logger } = useAppDependenciesContext();

  return useCallback(
    (
      participant: RemoteParticipant,
      {
        onParticipantDisconnect,
      }: {
        onParticipantDisconnect?: (participant: RemoteParticipant) => void;
      } = {},
    ) => {
      const hasParticipant = hasParticipantBySid(
        getParticipants(),
        participant,
      );

      // Ignore disconnect events for participant instances that are not
      // stored to avoid edge cases where a ghost disconnect event affects
      // rejoined participants. In general, a disconnect event for a
      // participant that the app is not tracking should be a no-op.
      if (!hasParticipant) {
        logger.warn(
          "Ignoring disconnect event for unknown participant",
          remoteParticipantToDiagnosticInfo(participant),
        );
        return;
      }

      sendLoggingEvents(
        TrackingEvents.REMOTE_PARTICIPANT_DISCONNECTED,
        {
          participant: remoteParticipantToDiagnosticInfo(participant),
        },
        { logLevel: "info", message: "Remote participant disconnected" },
      );

      setParticipants((prev) =>
        filterOutByParticipantSid(prev, participant.sid),
      );
      setRemoteParticipantConnectionStatesById((prev) =>
        omit(prev, [parseIdentity(participant.identity).id]),
      );
      onParticipantDisconnect?.(participant);
    },
    [
      getParticipants,
      logger,
      sendLoggingEvents,
      setParticipants,
      setRemoteParticipantConnectionStatesById,
    ],
  );
}
